import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useQueryClient } from '@tanstack/react-query'
import {
	alertTypeDefinitionsByCode,
	type CapSeverity,
	type KnownAlertTypeCode,
	type NWSSeverity,
	NWSSeveritySchema,
} from '@xweather/protect-types'
import { debounce, minBy } from 'es-toolkit'
import type { ComponentProps, ReactNode } from 'react'
import React, { memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Countdown } from '../../../components/Countdown.tsx'
import { I18n } from '../../../components/I18n.tsx'
import { Icon } from '../../../components/Icon.tsx'
import { IconStack } from '../../../components/IconStack.tsx'
import { Stack } from '../../../components/Stack.tsx'
import type { AnyAlert, HailAlert, LightningAlert, PrecipitationAlert, WeatherHazardAlert, WindAlert } from '../../../data/alerts/types.ts'
import { useAlerts } from '../../../data/alerts/useAlerts.ts'
import { useDeviceCombinedStatuses } from '../../../data/devices.ts'
import { isAssetLocation, isDeviceLocation, type Location } from '../../../data/locations.tsx'
import { type MeasurementAlertConfiguration, useMeasurementAlertsRecord } from '../../../data/measurementAlerts.ts'
import { type MeasurementType, measurementIcons, parameterToUnit } from '../../../data/measurementTypes.ts'
import { LTZ_SUFFIX } from '../../../data/protections/lightning.ts'
import { useProtection } from '../../../data/protections/protections.ts'
import { type AnyProtection, ProtectionType } from '../../../data/protections/types.ts'
import { useGetProtectionsByAsset } from '../../../data/protections/useProtections.ts'
import { dayjs } from '../../../dayjs.ts'
import { onsetBreach } from '../../alerts/precipitationAlert.ts'
import {
	calculateHailRange,
	degreesToCompass,
	getHighestPriorityHailContext,
	protectionIcons,
	useSortedProtections,
} from '../../alerts/utils.tsx'
import { UnitLabel } from '../../measurements/components/MeasurementLabel.tsx'
import { useConvertMeasurement } from '../../measurements/convertMeasurements.tsx'
import { useFormatDate } from '../../preferences/useFormatDate.tsx'
import { useRainRangeHelpers } from '../../rain-range-helpers.ts'

const alertInfoHandlers = {
	hail: (alert: HailAlert | undefined): ReactNode => <HailLabel alert={alert} />,
	lightning: (alert: LightningAlert | undefined): ReactNode => <LightningLabel alert={alert} />,
	precipitation: (alert: PrecipitationAlert | undefined): ReactNode => <PrecipitationLabel alert={alert} />,
	weatherHazard: (alert: WeatherHazardAlert | undefined): ReactNode => <WeatherHazardLabel alert={alert} />,
	wind: (alert: WindAlert | undefined): ReactNode => <WindLabel alert={alert} />,
} as const

// Helper function to get expanded content with type safety
const getExpandedContent = (alert: AnyAlert | undefined): ReactNode => {
	if (!alert) return null

	switch (alert.type) {
		case 'hail':
			return alertInfoHandlers.hail(alert)
		case 'lightning':
			return alertInfoHandlers.lightning(alert)
		case 'weatherHazard':
			return alertInfoHandlers.weatherHazard(alert)
		case 'wind':
			return alertInfoHandlers.wind(alert)
		case 'precipitation':
			return alertInfoHandlers.precipitation(alert)
		default: {
			alert satisfies never
			return null
		}
	}
}

export const AlertStack = memo(({ location, ...props }: { location: Location } & Omit<ComponentProps<typeof IconStack>, 'items'>) => {
	const theme = useTheme()
	const protections = useGetProtectionsByAsset(isAssetLocation(location) ? location.assetVrn : '')
	const { alerts } = useAlerts()
	const alertsForLocation = protections.map((p) => alerts[p.vrn]).filter((a) => a != null)
	const deviceStatuses = useDeviceCombinedStatuses()
	const measurementAlerts = useMeasurementAlertsRecord()
	const measurementAlertsForLocation = isDeviceLocation(location) ? measurementAlerts[location.id] : undefined
	const { activeProtections: baseActiveProtections, inactiveProtections: baseInactiveProtections } = useSortedProtections(protections)

	// Deduplicate inactive protections by type (excluding types already active)
	const deduplicateByType = useCallback(
		(protections: AnyProtection[]) => {
			return protections.filter(
				(protection, index, self) =>
					!baseActiveProtections.some((p) => p.type === protection.type && p.parameterType === protection.parameterType) &&
					index === self.findIndex((p) => p.type === protection.type && p.parameterType === protection.parameterType),
			)
		},
		[baseActiveProtections],
	)

	// Filter out forecast lightning if realtime exists
	const activeProtections = baseActiveProtections.filter((p) => {
		if (p.type === ProtectionType.Lightning && p.parameterType === 'forecast') {
			const realtimeVrn = p.vrn.replace(LTZ_SUFFIX, '')
			return !baseActiveProtections.some((realtimeProtection) => realtimeProtection.vrn === realtimeVrn)
		}
		return true
	})

	const inactiveProtections = deduplicateByType(baseInactiveProtections)
	const activeMeasurementAlerts = measurementAlertsForLocation?.active ?? []
	const inactiveMeasurementAlerts = measurementAlertsForLocation?.inactive ?? []
	const totalActiveCount = activeProtections.length + activeMeasurementAlerts.length
	const maxItems = Math.max(5, totalActiveCount)

	return (
		<IconStack
			maxItems={maxItems}
			items={[
				...activeProtections
					.map((protection) => {
						const alert = alerts[protection.vrn]
						if (!alert) return null
						const variant = shouldBeFilled(alert, protection) ? ('filled' as const) : ('outlined' as const)
						return {
							id: protection.vrn,
							variant: variant,
							icon: protectionIcons[protection.type],
							expandedContent: getExpandedContent(alertsForLocation.find((a) => a.protectionVrn === protection.vrn)),
							iconColor: theme.palette.alerts.alertStack.active[alert.severity][variant].icon,
							bgColor: theme.palette.alerts.alertStack.active[alert.severity][variant].bg,
							fontColor: theme.palette.alerts.alertStack.active[alert.severity][variant].font,
						}
					})
					.filter((item): item is NonNullable<typeof item> => item != null),
				...activeMeasurementAlerts.map((alert) => {
					const rule = alert.config.simpleRules[0]
					const icon = rule ? measurementIcons[rule.parameterName] : protectionIcons.measurement
					const isInvalid =
						deviceStatuses[alert.config.context.sourceId]?.status === 'OFFLINE' ||
						deviceStatuses[alert.config.context.sourceId]?.status === 'ERROR'
					return {
						id: alert.config.id,
						icon: icon ?? protectionIcons.measurement,
						variant: 'filled' as const,
						expand: isInvalid,
						expandedContent: isInvalid ? <Invalid /> : <MeasurementLabel config={alert.config} currentValue={alert.value} />,
						...(!isInvalid && {
							iconColor: theme.palette.alerts.alertStack.active.alarm.measurement.icon,
							bgColor: theme.palette.alerts.alertStack.active.alarm.measurement.bg,
							fontColor: theme.palette.alerts.alertStack.active.alarm.measurement.font,
						}),
					}
				}),
				...inactiveProtections.map((protection) => {
					const variant = shouldBeFilled(undefined, protection) ? ('filled' as const) : ('outlined' as const)
					return {
						id: protection.vrn,
						variant: variant,
						icon: protectionIcons[protection.type],
					}
				}),
				...inactiveMeasurementAlerts.map((alert) => {
					const rule = alert.config.simpleRules[0]
					const icon = rule ? measurementIcons[rule.parameterName] : protectionIcons.measurement
					const isInvalid =
						deviceStatuses[alert.config.context.sourceId]?.status === 'OFFLINE' ||
						deviceStatuses[alert.config.context.sourceId]?.status === 'ERROR'
					return {
						id: alert.config.id,
						icon: icon ?? protectionIcons.measurement,
						variant: 'filled' as const,
						expand: isInvalid,
						expandedContent: isInvalid ? <Invalid /> : undefined,
					}
				}),
			]}
			{...props}
		/>
	)
})

const LightningLabel = ({ alert }: { alert: LightningAlert | undefined }) => {
	const queryClient = useQueryClient()

	const debouncedInvalidateQuery = useMemo(
		() =>
			debounce(async () => {
				await queryClient.invalidateQueries({ queryKey: ['LIGHTNING_ALERTS'] })
			}, 200),
		[queryClient],
	)

	const handleTimeout = useCallback(async () => debouncedInvalidateQuery(), [debouncedInvalidateQuery])

	if (!alert) {
		return null
	}

	if (alert.context.LIGHTNING_THREAT_LEADTIME) {
		return (
			<Typography variant='body2'>
				<I18n i18nKey={($) => $.alerts.stormETA} values={{ eta: alert.context.LIGHTNING_THREAT_LEADTIME }} />
			</Typography>
		)
	}

	return (
		<Typography component='span' variant='body2'>
			<Countdown endTime={alert.eventTime} onTimeout={handleTimeout} />
		</Typography>
	)
}

const WeatherHazardLabel = ({ alert }: { alert: WeatherHazardAlert | undefined }) => {
	const severityCounts = alert?.context.reduce(
		(acc, ctx) => {
			const codeDefinition = alertTypeDefinitionsByCode[ctx.alertContextValue.WEATHER_HAZARD_TYPE as KnownAlertTypeCode]
			if (!codeDefinition) return acc
			const severity = codeDefinition?.region === 'us/canada' ? codeDefinition.nwsSeverity : codeDefinition?.capSeverity
			if (severity) acc[severity] = (acc[severity] ?? 0) + 1
			return acc
		},
		{ WARNING: 0, WATCH: 0, ADVISORY: 0, STATEMENT: 0, EXTREME: 0, SEVERE: 0, MODERATE: 0, MINOR: 0 } as Record<
			NWSSeverity | CapSeverity,
			number
		>,
	)
	return (
		<Stack gap={0.5}>
			{severityCounts &&
				(Object.entries(severityCounts) as [NWSSeverity | CapSeverity, number][])
					.filter(([_, count]) => count > 0)
					.map(([severity, count], index) => (
						<Stack key={severity} gap={0.5}>
							{index > 0 && <span>•</span>}
							<Typography variant='body2' textTransform='lowercase'>
								{count}{' '}
								{isNWSSeverity(severity) ? (
									<I18n i18nKey={($) => $.alerts.weatherHazard.nwsAlertSeverity[severity]} />
								) : (
									<I18n i18nKey={($) => $.alerts.weatherHazard.capSeverity[severity]} />
								)}
							</Typography>
						</Stack>
					))}
		</Stack>
	)
}

const WindLabel = ({ alert }: { alert: WindAlert | undefined }) => {
	const formatDate = useFormatDate()
	const time = alert?.context?.[0]?.time
	const windDirection = Number(alert?.context?.[0]?.alertContextValue.WIND_DIRECTION) ?? 0
	return (
		<Typography variant='body2'>{`${time ? formatDate(time, 'HH.mm') : '--:--'} ${degreesToCompass(windDirection)}:${windDirection}`}</Typography>
	)
}

const PrecipitationLabel = ({ alert }: { alert: PrecipitationAlert | undefined }) => {
	const { t } = useTranslation()
	const formatDate = useFormatDate()
	const { fromMmToUserPreference, formattedUnit } = useRainRangeHelpers(0, 0)
	const { data: protection } = useProtection({ vrn: alert?.protectionVrn ?? '' })
	if (!alert || alert.context.length === 0 || !protection) return null

	const onset = protection?.type === 'precipitation' ? onsetBreach(alert.context, protection) : undefined
	if (!onset) {
		const earliest = minBy(alert.context, (context) => dayjs(context.time).valueOf())
		return <Typography variant='body2'>{formatDate(earliest?.time ?? alert.startTime, 'HH.mm')}</Typography>
	}

	const window = t(($) => $.alerts.alertsPanel.precipitation.windowShort[onset.field])
	return (
		<Typography variant='body2'>
			{`${formatDate(onset.time, 'HH.mm')} ${fromMmToUserPreference(onset.value)} ${formattedUnit} / ${window}`}
		</Typography>
	)
}

const HailLabel = ({ alert }: { alert: HailAlert | undefined }) => {
	const closestContext = getHighestPriorityHailContext(alert?.context)
	const time = dayjs(closestContext?.time)
	const diff = Math.max(time.diff(dayjs(), 'minute'), 0)
	const range = calculateHailRange(diff)
	return (
		<Typography variant='body2'>
			<I18n i18nKey={($) => $.alerts.hailETA} values={{ eta: range }} />
		</Typography>
	)
}

const MeasurementLabel = ({ config, currentValue }: { config: MeasurementAlertConfiguration; currentValue?: number }) => {
	const convertFn = useConvertMeasurement()
	const rules = (() => {
		const rule = config.simpleRules[0]
		if (!rule) return undefined
		const [convertedValue, convertedMeasurementType] = convertFn(currentValue, {
			parameter: rule?.parameterName,
			unit: parameterToUnit[rule?.parameterName],
			statistic: rule?.statistic,
			period: rule?.period,
			height: rule?.height,
		} as MeasurementType)
		if (!convertedMeasurementType) return
		return {
			value: convertedValue,
			measurementType: convertedMeasurementType,
			threshold: 'maxValue' in rule.threshold ? ('ABOVE' as const) : ('BELOW' as const),
		}
	})()

	return (
		rules && (
			<React.Fragment>
				<Icon name={rules.threshold === 'ABOVE' ? 'ArrowUpFromLine' : 'ArrowDownFromLine'} sx={{ fontSize: 12 }} />
				<Typography variant='body2'>{rules.value?.toFixed(2)}</Typography>
				<UnitLabel measurementType={rules.measurementType} variant='body2' />
			</React.Fragment>
		)
	)
}

const Invalid = () => <Typography variant='body2'>{<I18n i18nKey={($) => $.alerts.measurement.invalid} />}</Typography>

const shouldBeFilled = (alert: AnyAlert | undefined, protection: AnyProtection): boolean => {
	if (alert && protection.type === ProtectionType.WeatherHazard) {
		return alert.severity === 'alarm'
	}
	return protection.parameterType === 'realtime'
}

const isNWSSeverity = (s: NWSSeverity | CapSeverity): s is NWSSeverity => NWSSeveritySchema.safeParse(s).success
